import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Crown, Zap, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [planType, setPlanType] = useState('pro');
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setPaymentStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= 10) {
      setPaymentStatus('timeout');
      return;
    }
    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`);
      if (res.data.plan_type) setPlanType(res.data.plan_type);
      if (res.data.payment_status === 'paid') {
        setPaymentStatus('success');
        const label = res.data.plan_type === 'ultimate' ? 'Ultimate' : 'Pro';
        toast({ title: 'Paiement confirme !', description: `Votre abonnement ${label} est maintenant actif` });
      } else if (res.data.status === 'expired') {
        setPaymentStatus('expired');
      } else {
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  };

  const isUltimate = planType === 'ultimate';
  const planLabel = isUltimate ? 'Ultimate' : 'Pro';
  const PlanIcon = isUltimate ? Zap : Crown;
  const iconColor = isUltimate ? 'text-purple-400' : 'text-yellow-400';
  const gradientFrom = isUltimate ? 'from-purple-500' : 'from-blue-500';
  const gradientTo = isUltimate ? 'to-violet-500' : 'to-cyan-500';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center p-6">
      <Toaster />
      <div className="bg-slate-800/50 border border-blue-500/20 rounded-3xl p-10 max-w-md w-full text-center" data-testid="payment-success-card">
        {paymentStatus === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-blue-400 mx-auto mb-6 animate-spin" />
            <h1 className="text-2xl font-bold text-white mb-2" data-testid="payment-checking-title">Verification du paiement...</h1>
            <p className="text-blue-300/70">Merci de patienter quelques instants</p>
          </>
        )}

        {paymentStatus === 'success' && (
          <>
            <div className={`w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center shadow-lg shadow-green-500/30`}>
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            <div className="flex items-center justify-center gap-2 mb-4">
              <PlanIcon className={`w-6 h-6 ${iconColor}`} />
              <span className={`${iconColor} font-semibold`} data-testid="payment-plan-label">{planLabel}</span>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2" data-testid="payment-success-title">
              Bienvenue dans l'univers {planLabel} !
            </h1>
            <p className="text-blue-300/70 mb-8">
              {isUltimate
                ? 'Votre abonnement Ultimate est actif. Profitez du transfert P2P direct et de toutes les fonctionnalites Pro.'
                : 'Votre abonnement Pro est maintenant actif. Vous pouvez personnaliser le fond d\'ecran de vos telechargements.'}
            </p>
            <Button
              onClick={() => navigate(isUltimate ? '/' : '/admin/background')}
              className={`w-full bg-gradient-to-r ${gradientFrom} ${gradientTo} hover:opacity-90 text-white font-semibold py-6`}
              data-testid="payment-success-cta"
            >
              <PlanIcon className="w-5 h-5 mr-2" />
              {isUltimate ? 'Essayer le transfert P2P' : 'Personnaliser mon fond d\'ecran'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </>
        )}

        {paymentStatus === 'timeout' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 bg-yellow-500/20 rounded-full flex items-center justify-center">
              <Loader2 className="w-12 h-12 text-yellow-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Paiement en cours de traitement</h1>
            <p className="text-blue-300/70 mb-6">
              Le paiement est en cours de verification. Vous recevrez un email de confirmation.
            </p>
            <Button onClick={() => navigate('/')} variant="outline" className="border-blue-500/30 text-blue-300" data-testid="payment-timeout-btn">
              Retour a l'accueil
            </Button>
          </>
        )}

        {(paymentStatus === 'error' || paymentStatus === 'expired') && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 bg-red-500/20 rounded-full flex items-center justify-center">
              <span className="text-4xl text-red-400"><CheckCircle className="w-12 h-12 rotate-45" /></span>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Paiement non trouve</h1>
            <p className="text-blue-300/70 mb-6">
              Nous n'avons pas pu verifier votre paiement. Veuillez reessayer ou contacter le support.
            </p>
            <Button onClick={() => navigate('/pricing')} variant="outline" className="border-blue-500/30 text-blue-300" data-testid="payment-error-btn">
              Reessayer
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
